import { LicenseLine } from "@/components/LicenseLine";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { findCasino } from "@/data/casinos";
import { AffiliateCTA } from "./AffiliateCTA";
import { BrandLogo } from "./BrandLogo";
import { RatingStars } from "./RatingStars";
import { casinoReviewPath } from "@/lib/format";

type Variant =
  | "band"      // wide horizontal band — ideal above the footer
  | "side"      // sidebar / inline card
  | "compact";  // tight 1-line strip

type Props = {
  brandSlug?: string;
  source?: string;
  placement?: string;
  variant?: Variant;
  className?: string;
};

export function PriorityPartner({
  brandSlug = "betinia",
  source = "priority-partner",
  placement = "band",
  variant = "band",
  className = "",
}: Props) {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const casino = findCasino(brandSlug);
  if (!casino) return null;

  const reviewHref = casinoReviewPath(prefix, lang, casino.slug);
  const bonus = isSv ? casino.bonus_summary_sv : casino.bonus_summary_en;
  const intro = isSv ? casino.intro_sv : casino.intro_en;

  if (variant === "compact") {
    return (
      <aside
        className={`flex items-center gap-3 rounded-2xl border border-gold-300/60 bg-gradient-to-r from-gold-200/40 to-white p-3 ${className}`}
        aria-label={t("label.priorityBrand")}
      >
        <BrandLogo
          slug={casino.slug}
          name={casino.name}
          src={casino.logo_url}
          size={40}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink-900">
            <span className="pill-gold mr-2 align-middle text-[10px]">
              ★ {t("label.priorityBrand")}
            </span>
            {casino.name}
          </p>
          <p className="line-clamp-1 text-xs text-ink-600">{bonus ?? intro}</p>
          <LicenseLine casino={casino} />
        </div>
        <AffiliateCTA
          brandSlug={casino.slug}
          source={source}
          placement={placement}
          size="md"
          label={t("cta.visitCasino")}
        />
      </aside>
    );
  }

  if (variant === "side") {
    return (
      <aside
        className={`overflow-hidden rounded-2xl border border-gold-300/60 bg-white shadow-card ${className}`}
        aria-label={t("label.priorityBrand")}
      >
        <div className="bg-gradient-to-br from-gold-300 to-gold-500 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-ink-950">
          ★ {t("label.priorityBrand")}
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3">
            <BrandLogo
              slug={casino.slug}
              name={casino.name}
              src={casino.logo_url}
              size={56}
            />
            <div>
              <h3 className="text-base font-bold text-ink-900">
                <Link to={reviewHref} className="text-ink-900 no-underline hover:text-brand-700">
                  {casino.name}
                </Link>
              </h3>
              <RatingStars value={casino.overall_rating} size="sm" />
            </div>
          </div>
          {bonus && (
            <p className="mt-3 text-sm text-ink-700">
              <span className="font-semibold">
                {isSv ? "Bonus: " : "Bonus: "}
              </span>
              {bonus}
            </p>
          )}
          <LicenseLine casino={casino} />
          <div className="mt-3 flex flex-wrap gap-1.5">
            {casino.swedish_license && (
              <span className="pill-success">{t("label.swedishLicense")}</span>
            )}
            {casino.spelpaus_connected && (
              <span className="pill-success">{t("label.spelpausConnected")}</span>
            )}
            {casino.swish_supported && <span className="pill-brand">Swish</span>}
            {casino.bankid_supported && <span className="pill-brand">BankID</span>}
          </div>
          <div className="mt-4 grid gap-2">
            <AffiliateCTA
              brandSlug={casino.slug}
              source={source}
              placement={placement}
              size="md"
            />
            <Link to={reviewHref} className="btn-soft w-full text-center text-sm">
              {t("cta.readReview")}
            </Link>
          </div>
          <p className="mt-3 text-[11px] text-ink-500">
            18+ · {isSv ? "Spela ansvarsfullt" : "Gamble responsibly"} ·{" "}
            <Link to={`${prefix}/spelpaus`} className="text-ink-500 underline-offset-2">
              Spelpaus.se
            </Link>
          </p>
        </div>
      </aside>
    );
  }

  // band: wide horizontal — used above the footer
  return (
    <section
      className={`relative isolate overflow-hidden rounded-3xl ${className}`}
      aria-label={t("label.priorityBrand")}
    >
      <div className="absolute inset-0 hero-mesh" />
      <div
        className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full bg-gold-500/30 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-32 -bottom-32 h-72 w-72 rounded-full bg-brand-500/30 blur-3xl"
        aria-hidden
      />
      <div className="relative grid gap-6 p-6 text-white md:grid-cols-12 md:items-center md:p-8">
        <div className="md:col-span-2 flex md:justify-center">
          <BrandLogo
            slug={casino.slug}
            name={casino.name}
            src={casino.logo_url}
            size={88}
            rounded="2xl"
          />
        </div>
        <div className="md:col-span-7">
          <span className="pill-gold pulse-soft text-[11px]">
            ★ {t("label.priorityBrand")}
          </span>
          <h3 className="mt-3 text-2xl font-extrabold leading-tight md:text-3xl">
            {isSv ? (
              <>
                {casino.name} —{" "}
                <span className="gradient-text-gold">
                  utvald partner på CasinoMenu.se
                </span>
              </>
            ) : (
              <>
                {casino.name} —{" "}
                <span className="gradient-text-gold">
                  CasinoMenu.se featured partner
                </span>
              </>
            )}
          </h3>
          <p className="mt-2 max-w-2xl text-sm text-white/85">
            {bonus ?? intro}
          </p>
          <LicenseLine casino={casino} />
          <div className="mt-3 flex flex-wrap gap-1.5">
            {casino.swedish_license && (
              <span className="pill-dark">{t("label.swedishLicense")}</span>
            )}
            {casino.spelpaus_connected && (
              <span className="pill-dark">{t("label.spelpausConnected")}</span>
            )}
            {casino.swish_supported && <span className="pill-dark">Swish</span>}
            {casino.bankid_supported && <span className="pill-dark">BankID</span>}
            {casino.trustly_supported && <span className="pill-dark">Trustly</span>}
          </div>
        </div>
        <div className="flex flex-col gap-2 md:col-span-3">
          <AffiliateCTA
            brandSlug={casino.slug}
            source={source}
            placement={placement}
            size="lg"
          />
          <Link
            to={reviewHref}
            className="btn-outline w-full text-center"
          >
            {t("cta.readReview")}
          </Link>
          <p className="text-center text-[11px] text-white/60">
            {t("affiliate.disclosure")}
          </p>
        </div>
      </div>
    </section>
  );
}
